import React from 'react';
import { Plus } from 'lucide-react';
import type { Rate } from '@/hooks/useGetRates';

interface QuickAddItemsProps {
  availableRates: Rate[];
  onAddItem: (description: string, unitPrice: number) => void;
  disabled?: boolean;
}

// Format service type: convert UPPERCASE_SNAKE to Title Case
const formatServiceName = (serviceType: string): string => {
  return serviceType
    .replace(/_/g, ' ')
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Special services that are auto-populated by template logic - exclude from quick add
const SPECIAL_SERVICES = [
  'Tow Base',
  'Tow Half Hour',
  'Tow Quarter Hour',
  'Tow Hourly',
  'Tow Hour Minimum',
  'Storage Base',
  'Storage Daily',
  'Labor Quarter Hour',
];

const QuickAddItems: React.FC<QuickAddItemsProps> = ({
  availableRates,
  onAddItem,
  disabled = false,
}) => {
  // Filter out special services and rates with zero value
  const filteredRates = availableRates.filter((rate) => {
    const displayName = formatServiceName(rate.serviceType);
    const isSpecialService = SPECIAL_SERVICES.includes(displayName);
    const hasZeroValue = rate.any === 0;
    return !isSpecialService && !hasZeroValue;
  });

  if (filteredRates.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <h4 className="text-sm font-bold text-black mb-2">Quick add items</h4>
      <div className="space-y-2">
        {filteredRates.map((rate) => {
          const displayName = formatServiceName(rate.serviceType);
          const displayPrice = rate.any || 0;

          return (
            <button
              key={rate.id}
              onClick={() => onAddItem(displayName, displayPrice)}
              disabled={disabled}
              className="w-full text-left p-3 bg-background rounded-lg border border-gray-200 shadow-md hover:bg-gray-50 transition-colors flex justify-between items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="flex items-center gap-2 text-sm text-gray-900">
                <Plus size={14} className="text-primary" />
                {displayName}
              </span>
              <span className="text-sm text-gray-500">
                ${displayPrice.toFixed(2)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default QuickAddItems;

